import MarkedReact from "marked-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BoardAiApi, BoardAiAskResult } from "../../../services/board-ai-api";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KuikaSkeletonList } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import AiSparkleIcon from "./ai-sparkle-icon";
import { SAFE_MARKDOWN_RENDERER } from "./safe-markdown";

interface AskAiModalProps {
  open: boolean;
  onClose: () => void;
}

// Starter prompts shown on the empty state. Phrased to match what the backend can actually answer
// (counts/structure from the board overview + content from the issues), so they're not dead ends.
const EXAMPLE_QUESTIONS: string[] = [
  "What's blocking the current work?",
  "How many open issues are on this board?",
  "Which issues have linked dependencies?",
  "What shipped recently?"
];

// Board-level "ask the board": a question answered from the workspace's issues, with the cited
// issues shown as chips that navigate to the full issue. Read-only — it never mutates the board.
const AskAiModal: React.FC<AskAiModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { domainName } = useParams<{ domainName: string }>();

  const [question, setQuestion] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);
  const [result, setResult] = useState<BoardAiAskResult | null>(null);

  useEffect((): void => {
    if (!open) return;
    setQuestion("");
    setBusy(false);
    setResult(null);
  }, [open]);

  const ask = (override?: string): void => {
    const trimmed: string = (override ?? question).trim();
    if (!trimmed || busy) return;
    if (override !== undefined) {
      setQuestion(override);
    }
    setBusy(true);
    setResult(null);
    void BoardAiApi.ask(trimmed)
      .then((value: BoardAiAskResult): void => setResult(value))
      .catch((): void => undefined)
      .finally((): void => setBusy(false));
  };

  return (
    <KModal
      showModal={open}
      closeModal={onClose}
      width={600}
      header={
        <KuikaH5>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <AiSparkleIcon size={16} />
            Ask the board
          </span>
        </KuikaH5>
      }
    >
      <div className="kuika-board__ask">
        <div className="kuika-board__ask-hero">
          <div className={`kuika-board__ask-field${busy ? " is-busy" : ""}`}>
            <AiSparkleIcon size={16} className="kuika-board__ask-field-icon" />
            <input
              className="kuika-board__ask-input"
              value={question}
              placeholder="Ask anything about this board…"
              autoFocus
              disabled={busy}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuestion(event.target.value)}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  ask();
                }
              }}
            />
            <button
              type="button"
              className="kuika-board__ask-send"
              onClick={() => ask()}
              disabled={!question.trim() || busy}
              aria-label="Ask"
              title="Ask"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M8 13V3M8 3L3.5 7.5M8 3l4.5 4.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="kuika-board__ask-hint">Answers are drawn only from this board's issues.</div>
        </div>

        {!result && !busy && (
          <div className="kuika-board__ask-suggestions">
            <span className="kuika-board__ask-suggestions-label">Try asking</span>
            <div className="kuika-board__ask-chips">
              {EXAMPLE_QUESTIONS.map((example: string) => (
                <button key={example} type="button" className="kuika-board__ask-chip" onClick={() => ask(example)}>
                  {example}
                </button>
              ))}
            </div>
          </div>
        )}

        {busy && (
          <div className="kuika-board__ask-loading" role="status" aria-live="polite">
            <AiSparkleIcon size={15} className="kuika-board__ai-spin" />
            {/* The answer's lines; the card around it announces the wait ("Reading the board…"). */}
            <KuikaSkeletonList rows={3} withIcon={false} density="compact" />
            <span className="kuika-board__ask-loading-text">Reading the board…</span>
          </div>
        )}

        {result && !busy && (
          <div className="kuika-board__ask-answer">
            <div className="kuika-board__ask-answer-label">
              <AiSparkleIcon size={13} />
              Answer
            </div>
            <div className="kuika-board__ask-answer-body">
              <MarkedReact gfm breaks value={result.answer} renderer={SAFE_MARKDOWN_RENDERER} />
            </div>
            {result.sources.length > 0 && (
              <div className="kuika-board__ask-sources">
                <span className="kuika-board__ask-sources-label">Sources</span>
                <div className="kuika-board__ask-source-list">
                  {result.sources.map((source) => (
                    <button
                      key={source.key}
                      type="button"
                      className="kuika-board__ask-source"
                      title={source.summary}
                      onClick={() => navigate(`/${domainName}/issue/${source.key}`)}
                    >
                      {source.key}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </KModal>
  );
};

export default AskAiModal;
